import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { Sidebar } from './components/sidebar/Sidebar.jsx'
import { ApiError, onUnauthorized } from './lib/api.js'
import { meQuery } from './lib/queries/session.js'
import { projectsQuery } from './lib/queries/projects.js'
import { supportTicketsQuery } from './lib/queries/support.js'
import { ChatPage } from './pages/ChatPage.jsx'
import { HubPage } from './pages/HubPage.jsx'
import { LibraryIndexPage, LibraryPage } from './pages/LibraryPage.jsx'
import { LoginPage } from './pages/LoginPage.jsx'
import { NewQuestionPage } from './pages/NewQuestionPage.jsx'
import { NotFoundPage } from './pages/NotFoundPage.jsx'
import { SettingsPage } from './pages/SettingsPage.jsx'
import { SharePage } from './pages/SharePage.jsx'
import { ProjectsPage } from './pages/ProjectsPage.jsx'
import { SupportPage } from './pages/SupportPage.jsx'
import p from './pages/pages.module.css'

const str = (value) => (typeof value === 'string' && value ? value : undefined)

const rootRoute = createRootRouteWithContext()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search) => ({ next: str(search.next), error: str(search.error) }),
  component: LoginPage,
})

/** Signed-in routes: loads the session once and redirects to /login on 401. */
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery)
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        throw redirect({ to: '/login', search: { next: location.href } })
      }
      throw error
    }
  },
  component: SignedIn,
})

function SignedIn() {
  const navigate = useNavigate()

  // Session expired mid-use → back to sign-in, then here again.
  useEffect(
    () =>
      onUnauthorized(() => {
        const next = `${window.location.pathname}${window.location.search}`
        navigate({ to: '/login', search: { next } })
      }),
    [navigate],
  )

  return <Outlet />
}

/** DCKAP Hub home: the app picker, without the Knowledge Base sidebar. */
const hubRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/',
  component: HubPage,
})

/** Support app: its own layout (history rail + ticket workspace). */
const supportRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/support',
  // Warm the history while the page opens (hover on the Hub card too); never blocks or fails navigation.
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(supportTicketsQuery)
  },
  component: SupportPage,
})

/** Project Management app: its own layout (projects sidebar + workspace). */
const projectsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/projects',
  validateSearch: (search) => ({
    p: str(search.p),
    tab: str(search.tab),
    view: search.view === 'weekly' || search.view === 'blocked' ? search.view : undefined,
  }),
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(projectsQuery)
  },
  component: ProjectsPage,
})

/** Knowledge Base layout: sidebar + page. */
const appRoute = createRoute({
  getParentRoute: () => authRoute,
  id: 'app',
  component: AppShell,
})

function AppShell() {
  const navigate = useNavigate()

  // ⌘K / Ctrl+K → new question.
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        navigate({ to: '/new' })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  return (
    <div className={p.shell}>
      <Sidebar />
      <main className={p.main}>
        <Outlet />
      </main>
    </div>
  )
}

const newRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/new',
  validateSearch: (search) => ({ system: str(search.system) }),
  component: NewQuestionPage,
})

const chatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/c/$conversationId',
  component: ChatPage,
})

const libraryIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/library',
  component: LibraryIndexPage,
})

const libraryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/library/$slug',
  validateSearch: (search) => ({
    file: str(search.file),
    edit: search.edit === true || search.edit === 'true' || search.edit === 1 ? true : undefined,
    new: search.new === true || search.new === 'true' ? true : undefined,
  }),
  component: LibraryPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsPage,
})

const shareRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/s/$shareId',
  component: SharePage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  authRoute.addChildren([
    hubRoute,
    supportRoute,
    projectsRoute,
    appRoute.addChildren([newRoute, chatRoute, libraryIndexRoute, libraryRoute, settingsRoute, shareRoute]),
  ]),
])

export function createAppRouter(queryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Data freshness is TanStack Query's job, not the router's.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  })
}
